import { Group, Stack, Text, Paper, Badge, ActionIcon, Card, Tooltip, Button, Modal } from '@mantine/core';
import refreshIcon from '../assets/icons/refresh.svg';
import bankIcon from '../assets/icons/bank.svg';
import trashIcon from '../assets/icons/trash.svg';
import plusIcon from '../assets/icons/plus.svg';
import shareIcon from '../assets/icons/share.svg';
import { env } from '../utils/env';
import type { Account, Balance } from '../types/account';
import { notifications } from '@mantine/notifications';
import { useState, useEffect } from 'react';
import AccountModal from './AccountModal';
import { useSharedResource } from '../hooks/use-shared-resource';
import { ShareModal } from './ShareModal';
import { SharedMembersList } from './SharedMembersList';
import { User } from '@propelauth/react';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit, PlaidLinkError } from 'react-plaid-link';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import LinkUpdateHandler from './LinkUpdateHandler';
import ConsentDialog from './ConsentDialog';
import { useItems } from '../hooks/useItems';
import { useQueryClient } from '@tanstack/react-query';

// Add missing type definition
interface SharedMember {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'active' | 'declined';
}

interface AccountListProps {
  accounts: Account[];
  onRefresh: () => void;
  loading?: boolean;
  accessToken: string;
  currentUser: User; // PropelAuth User type already includes userId
}

// Helper functions
const getStatusColor = (status: Account['status']): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green';
    case 'error':
      return 'red';
    case 'pending':
      return 'yellow';
    case 'needs_reauth':
      return 'orange';
    case 'needs_auth_refresh':
      return 'orange';
    case 'pending_disconnect':
      return 'orange';
    case 'pending_expiration':
      return 'orange';
    case 'revoked':
      return 'red';
    case 'verification_expired':
      return 'red';
    case 'needs_account_selection':
      return 'blue';
    case 'closed':
      return 'gray';
    default:
      return 'gray';
  }
};

const formatBalance = (balance: Balance | undefined): string => {
  if (!balance?.amount || !balance?.currency) return '--';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: balance.currency
    }).format(balance.amount);
  } catch (error) {
    console.error('Error formatting balance:', error);
    return `${balance.amount} ${balance.currency}`;
  }
};

const AccountList = ({ accounts, onRefresh, loading = false, accessToken, currentUser }: AccountListProps) => {
  const queryClient = useQueryClient();
  const { deleteItem, isDeleting, getRefreshToken, isGettingRefreshToken } = useItems(accessToken);

  const [modalOpened, setModalOpened] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const [reauthorizeItemId, setReauthorizeItemId] = useState<string | null>(null);
  const [showUpdateMode, setShowUpdateMode] = useState(false);
  const [localAccounts, setLocalAccounts] = useState<Account[]>([]);
  const [accountSelectionEnabled, setAccountSelectionEnabled] = useState(false);

  // New state for delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string } | null>(null);

  // Add shared resource management for selected account
  const {
    members,
    loading: membersLoading,
    handleShare,
    handleRemoveMember,
  } = useSharedResource(
    'account',
    selectedAccount?.id || ''
  );

  // Add new state for consent dialog
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [consentItem, setConsentItem] = useState<{
    id: string;
    name: string;
    productsRequiringConsent: string[];
  } | null>(null);

  // Update local accounts when props change
  useEffect(() => {
    setLocalAccounts(accounts);
  }, [accounts]);

  // Handle checking for items that need additional consent
  useEffect(() => {
    if (!accounts) return;

    // Check for items that need additional consent
    const itemNeedingConsent = accounts.find(account =>
      account.item && account.item.status === 'needs_additional_consent' &&
      account.item.pending_consent_products &&
      account.item.pending_consent_products.length > 0
    );

    if (itemNeedingConsent && itemNeedingConsent.item) {
      setConsentItem({
        id: itemNeedingConsent.item.id,
        name: itemNeedingConsent.item.institution_name || 'Your financial institution',
        productsRequiringConsent: itemNeedingConsent.item.pending_consent_products || []
      });
      setShowConsentDialog(true);
    }
  }, [accounts]);

  // Handle Plaid reauthorization using React Query
  const handleReauthorize = async (itemId: string, accountSelectionEnabled: boolean = false) => {
    if (!accessToken) return;

    try {
      setReauthorizeItemId(itemId);
      setIsReauthorizing(true);
      setAccountSelectionEnabled(accountSelectionEnabled);

      // Use the getRefreshToken mutation
      const result = await getRefreshToken({ itemId, accountSelectionEnabled });
      setLinkToken(result.linkToken);

      // Store link token in localStorage for OAuth redirect flow
      localStorage.setItem('plaid_link_token', result.linkToken);

      // Show the update mode component
      setShowUpdateMode(true);
    } catch (err) {
      console.error('Error initiating reauthorization:', err);
      showErrorNotification(err, 'Failed to reconnect account. Please try again.');
      setIsReauthorizing(false);
      setReauthorizeItemId(null);
      setAccountSelectionEnabled(false);
    }
  };

  // Handle successful reauthorization
  const handleReauthorizeSuccess = () => {
    // Refresh accounts list to get the correct status from the server
    // instead of manually setting it to 'active'
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    onRefresh();

    // Reset state
    setIsReauthorizing(false);
    setReauthorizeItemId(null);
    setLinkToken(null);
    setShowUpdateMode(false);
    setAccountSelectionEnabled(false);

    showSuccessNotification("Account successfully reconnected");
  };

  // Plaid Link configuration
  const config = {
    token: linkToken,
    onSuccess: async (public_token: string, metadata: any) => {
      try {
        // Exchange public token
        const response = await fetch(`${env.API_URL}/api/financial/link/exchange`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            public_token,
            metadata
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to reauthorize account');
        }

        showSuccessNotification('Successfully reconnected account');

        // Refresh accounts
        queryClient.invalidateQueries({ queryKey: ['accounts'] });
        onRefresh();
      } catch (err) {
        console.error('Error completing reauthorization:', err);
        showErrorNotification(err, 'Failed to reconnect account. Please try again.');
      } finally {
        setIsReauthorizing(false);
        setReauthorizeItemId(null);
        setLinkToken(null);
      }
    },
    onExit: (() => {
      setIsReauthorizing(false);
      setReauthorizeItemId(null);
      setLinkToken(null);
    }) as PlaidLinkOnExit,
    onError: ((error: PlaidLinkError) => {
      console.error('Plaid Link error:', error);

      // Special handling for INVALID_LINK_TOKEN
      if (error.error_code === 'INVALID_LINK_TOKEN' && reauthorizeItemId) {
        console.log('Link token expired, generating a new one...');
        // Automatically regenerate token
        handleReauthorize(reauthorizeItemId, accountSelectionEnabled);
        return;
      }

      notifications.show({
        title: 'Error',
        message: error.display_message || error.error_message || 'An error occurred during account reconnection',
        color: 'red',
      });
      setIsReauthorizing(false);
      setReauthorizeItemId(null);
      setLinkToken(null);
    }),
    // Add receivedRedirectUri if we have an OAuth state param in URL
    ...(window.location.href.includes('oauth_state_id=')
      ? { receivedRedirectUri: window.location.href }
      : {})
  };
  const { open, ready } = usePlaidLink(config);

  // Start link flow when token is ready
  useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  // Add missing handleShareAccount function
  const handleShareAccount = async (email: string) => {
    if (!selectedAccount) return;

    try {
      await handleShare(email);
      onRefresh();
      setShareModalOpen(false);
    } catch (error) {
      showErrorNotification(error, 'Failed to share account');
    }
  };

  const handleDeleteItem = async (itemId: string, institutionName: string) => {
    // Open the delete confirmation modal instead of using browser confirm
    setItemToDelete({ id: itemId, name: institutionName });
    setDeleteModalOpen(true);
  };

  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    try {
      // Use the deleteItem mutation from useItems
      deleteItem(itemToDelete.id);
      // Success notification is handled in the mutation
    } catch (err) {
      // Error handling is done in the mutation
      console.error('Error in delete flow:', err);
    } finally {
      // Close the modal and clear the item to delete
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCreateManualAccount = async (values: any) => {
    try {
      const response = await fetch(`${env.API_URL}/api/financial/accounts/manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: values.name,
          account_type: values.account_type,
          account_subtype: values.account_subtype,
          current_balance: {
            amount: values.current_balance,
            currency: values.currency_code,
          },
          available_balance: {
            amount: values.current_balance,
            currency: values.currency_code,
          },
          currency_code: values.currency_code,
          mask: values.mask,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create account');
      }

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onRefresh();
      setModalOpened(false);
    } catch (err) {
      console.error('Error creating manual account:', err);
      showErrorNotification(err, 'Failed to create account');
    }
  };

  // Group accounts by institution and shared status
  const groupedAccounts = localAccounts.reduce((acc, account) => {
    const key = account.is_shared ? 'shared' : account.institution_name || 'Other';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(account);
    return acc;
  }, {} as Record<string, Account[]>);

  const handleConsentSuccess = () => {
    // Refresh accounts to get updated status
    queryClient.invalidateQueries({ queryKey: ['accounts'] });
    onRefresh();
  };

  return (
    <Stack>
      <Group justify="space-between" mb="md">
        <Text size="xl" fw={700}>Connected Accounts</Text>
        <Group>
          <Button
            leftSection={<img src={plusIcon} alt="Add" width={16} height={16} />}
            onClick={() => setModalOpened(true)}
            variant="light"
            size="sm"
          >
            Add Manual Account
          </Button>
          <Tooltip label="Refresh accounts">
            <ActionIcon
              variant="subtle"
              onClick={onRefresh}
              disabled={loading}
              loading={loading}
              title="Refresh accounts"
            >
              <img src={refreshIcon} alt="Refresh" width={16} height={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Render shared accounts first */}
      {groupedAccounts['shared']?.length > 0 && (
        <Card key="shared" withBorder mb="md">
          <Group gap="xs" mb="md" justify="space-between">
            <Group gap="xs">
              <img src={shareIcon} alt="" width={20} height={20} />
              <Text fw={500}>Shared Accounts</Text>
            </Group>
          </Group>
          {groupedAccounts['shared'].map(account => (
            <AccountCard
              key={account.id}
              account={account}
              onShare={() => {
                setSelectedAccount(account);
                setShareModalOpen(true);
              }}
              isOwner={account.shared_by_id === currentUser.userId}
              members={members}
              onRemoveMember={handleRemoveMember}
              getStatusColor={getStatusColor}
              formatBalance={formatBalance}
              onReauthorize={handleReauthorize}
              isReauthorizing={isReauthorizing && reauthorizeItemId === account.item_id}
            />
          ))}
        </Card>
      )}

      {/* Render institution groups */}
      {Object.entries(groupedAccounts)
        .filter(([key]) => key !== 'shared')
        .map(([institution, accounts]) => (
          <Card key={institution} withBorder mb="md">
            <Group gap="xs" mb="md" justify="space-between">
              <Group gap="xs">
                <img src={bankIcon} alt="" width={20} height={20} />
                <Text fw={500}>{institution}</Text>
              </Group>
              <Tooltip label={`Remove ${institution}`}>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    const itemId = accounts[0].item_id;
                    handleDeleteItem(itemId, institution);
                  }}
                  loading={isDeleting && itemToDelete?.id === accounts[0].item_id}
                >
                  <img src={trashIcon} alt="Remove" width={16} height={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            {accounts.map(account => (
              <AccountCard
                key={account.id}
                account={account}
                onShare={() => {
                  setSelectedAccount(account);
                  setShareModalOpen(true);
                }}
                isOwner={account.shared_by_id === currentUser.userId}
                members={members}
                onRemoveMember={handleRemoveMember}
                getStatusColor={getStatusColor}
                formatBalance={formatBalance}
                onReauthorize={handleReauthorize}
                isReauthorizing={isReauthorizing && reauthorizeItemId === account.item_id}
              />
            ))}
          </Card>
        ))}

      {/* Delete confirmation modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        title={`Disconnect ${itemToDelete?.name || 'account'}?`}
        centered
      >
        <Stack>
          <Text size="sm">
            This will permanently disconnect your account from {itemToDelete?.name}. You'll need to reconnect through Plaid if you want to access this account again.
          </Text>
          <Text size="sm">
            All associated accounts and transaction history will be removed.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => {
              setDeleteModalOpen(false);
              setItemToDelete(null);
            }}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDeleteItem} loading={isDeleting}>
              Disconnect
            </Button>
          </Group>
        </Stack>
      </Modal>

      <AccountModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSubmit={handleCreateManualAccount}
      />

      {/* Share Modal */}
      {selectedAccount && (
        <ShareModal
          type="account"
          resourceId={selectedAccount.id}
          resourceName={selectedAccount.name}
          opened={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedAccount(null);
          }}
          onShare={handleShareAccount}
          accessToken={accessToken}
        />
      )}

      {/* Add the LinkUpdateHandler when in update mode */}
      {showUpdateMode && linkToken && reauthorizeItemId && (
        <LinkUpdateHandler
          linkToken={linkToken}
          accessToken={accessToken}
          itemId={reauthorizeItemId}
          onSuccess={handleReauthorizeSuccess}
          isOauth={window.location.href.includes('oauth_state_id=')}
          accountSelectionEnabled={accountSelectionEnabled}
        />
      )}

      {/* Consent Dialog */}
      {consentItem && (
        <ConsentDialog
          itemId={consentItem.id}
          itemName={consentItem.name}
          productsRequiringConsent={consentItem.productsRequiringConsent}
          opened={showConsentDialog}
          onClose={() => setShowConsentDialog(false)}
          onSuccess={handleConsentSuccess}
          accessToken={accessToken}
        />
      )}
    </Stack>
  );
};

// Update AccountCard props
interface AccountCardProps {
  account: Account;
  onShare: () => void;
  isOwner: boolean;
  members: SharedMember[];
  onRemoveMember: (memberId: string) => Promise<void>;
  getStatusColor: (status: Account['status']) => string;
  formatBalance: (balance: Balance | undefined) => string;
  onReauthorize?: (itemId: string, accountSelectionEnabled?: boolean) => void;
  isReauthorizing?: boolean;
}

const AccountCard = ({
  account,
  onShare,
  isOwner,
  members,
  onRemoveMember,
  getStatusColor,
  formatBalance,
  onReauthorize,
  isReauthorizing = false
}: AccountCardProps) => {
  const [showMembers, setShowMembers] = useState(false);

  // Determine if account needs reconnection
  const needsReconnection = [
    'error',
    'needs_reauth',
    'pending_disconnect',
    'pending_expiration',
    'revoked',
    'verification_expired'
  ].includes(account.status);

  // Check if new accounts are available
  const hasNewAccounts = account.status === 'needs_account_selection';

  // Format consent expiration date
  const formattedExpirationDate = account.consent_expiration_time
    ? new Date(account.consent_expiration_time).toLocaleDateString()
    : null;

  return (
    <Paper key={account.id} withBorder p="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Stack gap="xs">
            <Group gap="xs">
              <Text fw={500}>{account.name}</Text>
              {account.mask && (
                <Text size="sm" c="dimmed">
                  •••• {account.mask}
                </Text>
              )}
              {account.is_shared && (
                <Badge
                  color="blue"
                  leftSection={<img src={shareIcon} alt="Shared" width={12} height={12} />}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowMembers(!showMembers)}
                >
                  Shared
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <Badge size="sm">{account.type}</Badge>
              {account.subtype && account.subtype !== account.type && (
                <Badge size="sm" color="gray">{account.subtype}</Badge>
              )}
              <Badge
                size="sm"
                color={getStatusColor(account.status)}
              >
                {account.status}
              </Badge>
            </Group>
            {(account.status === 'pending_expiration' || formattedExpirationDate) && (
              <Text size="xs" c="orange">
                Access to this account expires {formattedExpirationDate || 'soon'}. Please reconnect.
              </Text>
            )}
            {hasNewAccounts && (
              <Text size="xs" c="blue">
                New accounts are available from your financial institution.
              </Text>
            )}
          </Stack>

          <Stack align="flex-end" gap="xs">
            <Text fw={500} style={{ whiteSpace: 'nowrap' }}>
              {formatBalance(account.current_balance)}
            </Text>
            {account.available_balance &&
              account.available_balance.amount !== account.current_balance?.amount && (
                <Tooltip label="Available Balance">
                  <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    Available: {formatBalance(account.available_balance)}
                  </Text>
                </Tooltip>
              )}
            <Group>
              {needsReconnection && onReauthorize && (
                <Button
                  variant="filled"
                  size="xs"
                  color="red"
                  loading={isReauthorizing}
                  onClick={() => onReauthorize(account.item_id)}
                >
                  Reconnect
                </Button>
              )}
              {hasNewAccounts && onReauthorize && (
                <Button
                  variant="filled"
                  size="xs"
                  color="blue"
                  loading={isReauthorizing}
                  onClick={() => onReauthorize(account.item_id, true)}
                >
                  Select New Accounts
                </Button>
              )}
              {isOwner && (
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<img src={shareIcon} alt="Share" width={12} height={12} />}
                  onClick={onShare}
                >
                  Share
                </Button>
              )}
            </Group>
          </Stack>
        </Group>

        {/* Show members list when expanded */}
        {showMembers && account.is_shared && (
          <SharedMembersList
            type="account"
            resourceId={account.id}
            members={members}
            isOwner={isOwner}
            onRemoveMember={onRemoveMember}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default AccountList; 